/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useState } from 'react';
import type { ChangeEvent } from 'react';
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Form,
  FormGroup,
  Label,
  Spinner,
} from 'reactstrap';
import { createGlobalStyle } from 'styled-components';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import ptBR from 'date-fns/locale/pt-BR';
import { useEvent } from './../../../hooks/useEvent';
import { toast } from 'react-toastify';

import 'react-datepicker/dist/react-datepicker.css';
import { api } from '../../../services/api';
import LimitedTextarea from '../LimitedTextArea';
registerLocale('ptBR', ptBR);

interface PropsEvent {
  isOpen: boolean;
  handleIsOpen: () => void;
  getAllNotifications: (page: number) => Promise<void>;
}

const DatePickerWrapperStyles = createGlobalStyle`
  .date_picker.full-width {
    width: 100%;
  }
  .react-datepicker-popper {
    z-index: 9999 !important;
  }
`;

export function ModalAddNotification({
  isOpen,
  handleIsOpen,
  getAllNotifications,
}: PropsEvent): React.JSX.Element {
  const [title, setTitle] = useState<string>('');
  const [content, setContent] = useState<string>('');
  const [sendType, setSendType] = useState<'now' | 'schedule'>('now');
  const [scheduleDate, setScheduleDate] = useState<Date | null>(null);
  const [scheduleTime, setScheduleTime] = useState<string>('');
  const [notificationType, setNotificationType] = useState<'push' | 'popup'>('push');
  const [file, setFile] = useState<File | null>(null);
  const [popupText, setPopupText] = useState<string>('');

  // Errors
  const [errorTitle, setErrorTitle] = useState<string>('');
  const [errorContent, setErrorContent] = useState<string>('');
  const [errorScheduleDate, setErrorScheduleDate] = useState<string>('');
  const [errorScheduleTime, setErrorScheduleTime] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (): Promise<void> => {
    let hasError = false;

    if (!title) {
      setErrorTitle('Campo obrigatório.');
      hasError = true;
    } else {
      setErrorTitle('');
    }

    if (!content && notificationType === 'push') {
      setErrorContent('Campo obrigatório.');
      hasError = true;
    } else {
      setErrorContent('');
    }

    if (sendType === 'schedule') {
      if (!scheduleDate) {
        setErrorScheduleDate('Campo obrigatório.');
        hasError = true;
      } else {
        setErrorScheduleDate('');
      }

      if (!scheduleTime) {
        setErrorScheduleTime('Campo obrigatório.');
        hasError = true;
      } else {
        setErrorScheduleTime('');
      }
    }

    if (hasError) return;
    setLoading(true);
    const scheduledAt =
      sendType === 'schedule' && scheduleDate
        ? new Date(
            scheduleDate.setHours(
              Number(scheduleTime.split(':')[0]),
              Number(scheduleTime.split(':')[1]),
            ),
          )
        : null;

    const formattedDate = scheduledAt
      ? `${scheduledAt.getFullYear()}-${String(scheduledAt.getMonth() + 1).padStart(
          2,
          '0',
        )}-${String(scheduledAt.getDate()).padStart(2, '0')} ${String(
          scheduledAt.getHours(),
        ).padStart(2, '0')}:${String(scheduledAt.getMinutes()).padStart(2, '0')}`
      : null;

    try {
      const formData = new FormData();
      formData.append('titulo', title);
      if (notificationType === 'push') {
        formData.append('conteudo', content);
      }

      formData.append('data_hora', formattedDate || '');
      formData.append('tipo_notificacao', notificationType); // Ex: 'popup' ou 'push'

      if (notificationType === 'popup') {
        formData.append('texto_popup', popupText || '');
        if (file) {
          formData.append('file', file);
        }
      }

      await api.post('/notifications', formData);

      toast.success('Notificação criada com sucesso!!');
      await getAllNotifications(1);
      handleClose();
    } catch (error) {
      toast.error('Erro ao criar notificação!');
      console.error('Erro ao criar notificação:', error);
      // Aqui você pode adicionar um toast ou alerta
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (): void => {
    setTitle('');
    setContent('');
    setSendType('now');
    setScheduleDate(null);
    setScheduleTime('');
    setNotificationType('push');
    setFile(null);
    setPopupText('');

    setErrorTitle('');
    setErrorContent('');
    setErrorScheduleDate('');
    setErrorScheduleTime('');

    handleIsOpen();
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const selectedFile = e.target.files?.[0] ?? null;
    setFile(selectedFile);
  };

  const handleDateChange = (date: Date | null): void => {
    setScheduleDate(date);
  };

  const handleInputChange =
    (setter: React.Dispatch<React.SetStateAction<string>>) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
      setter(e.target.value);
    };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      <ModalHeader toggle={handleClose}>Cadastro de Notificação</ModalHeader>
      <ModalBody>
        <Form>
          <FormGroup>
            <Label style={{ color: errorTitle ? '#E44B39' : undefined }}>Título</Label>
            <Input
              value={title}
              onChange={handleInputChange(setTitle)}
              style={{ borderColor: errorTitle ? '#E44B39' : undefined }}
              placeholder="Insira o título da notificação"
            />
            {errorTitle && <div style={{ color: '#E44B39' }}>{errorTitle}</div>}
          </FormGroup>
          {notificationType === 'push' && (
            <FormGroup>
              <Label style={{ color: errorContent ? '#E44B39' : undefined }}>Conteúdo</Label>
              {/* <Input
              type="textarea"
              value={content}
              onChange={handleInputChange(setContent)}
              style={{ borderColor: errorContent ? '#E44B39' : undefined }}
              placeholder="Conteúdo da notificação"
            /> */}

              <LimitedTextarea
                value={content}
                onChange={setContent}
                placeholder="Conteúdo da notificação"
                error={Boolean(errorContent)}
              />

              {errorContent && <div style={{ color: '#E44B39' }}>{errorContent}</div>}
            </FormGroup>
          )}
          <FormGroup tag="fieldset">
            <Label>Tipo de envio</Label>
            <FormGroup check>
              <Label check>
                <Input
                  type="radio"
                  checked={sendType === 'now'}
                  onChange={(): void => {
                    setSendType('now');
                  }}
                />{' '}
                Notificar agora
              </Label>
            </FormGroup>
            <FormGroup check>
              <Label check>
                <Input
                  type="radio"
                  checked={sendType === 'schedule'}
                  onChange={(): void => {
                    setSendType('schedule');
                  }}
                />{' '}
                Agendar notificação
              </Label>
            </FormGroup>
          </FormGroup>

          {sendType === 'schedule' && (
            <>
              <FormGroup>
                <Label style={{ color: errorScheduleDate ? '#E44B39' : undefined }}>Data</Label>
                <ReactDatePicker
                  selected={scheduleDate}
                  onChange={handleDateChange}
                  wrapperClassName="date_picker full-width"
                  popperClassName="react-datepicker-popper"
                  dateFormat="dd/MM/yyyy"
                  locale="ptBR"
                  customInput={<Input />}
                  minDate={new Date()}
                />
                <DatePickerWrapperStyles />
                {errorScheduleDate && <div style={{ color: '#E44B39' }}>{errorScheduleDate}</div>}
              </FormGroup>

              <FormGroup>
                <Label style={{ color: errorScheduleTime ? '#E44B39' : undefined }}>Hora</Label>
                <Input
                  type="time"
                  value={scheduleTime}
                  onChange={handleInputChange(setScheduleTime)}
                  style={{ borderColor: errorScheduleTime ? '#E44B39' : undefined }}
                />
                {errorScheduleTime && <div style={{ color: '#E44B39' }}>{errorScheduleTime}</div>}
              </FormGroup>
            </>
          )}

          <FormGroup tag="fieldset">
            <Label>Tipo de notificação</Label>
            <FormGroup check>
              <Label check>
                <Input
                  type="radio"
                  checked={notificationType === 'push'}
                  onChange={(): void => {
                    setNotificationType('push');
                  }}
                />{' '}
                Push
              </Label>
            </FormGroup>
            <FormGroup check>
              <Label check>
                <Input
                  type="radio"
                  checked={notificationType === 'popup'}
                  onChange={(): void => {
                    setNotificationType('popup');
                  }}
                />{' '}
                Pop-up
              </Label>
            </FormGroup>
          </FormGroup>

          {notificationType === 'popup' && (
            <FormGroup>
              <Label>Imagem (opcional)</Label>
              <Input type="file" accept="image/*" onChange={handleFileChange} />
              {file && (
                <div style={{ marginTop: 10 }}>
                  <img
                    src={URL.createObjectURL(file)}
                    alt="Preview"
                    style={{ width: '50%', borderRadius: 8 }}
                  />
                </div>
              )}
              <Label className="mt-3">Ou texto exibido após login</Label>
              {/* <Input
                type="textarea"
                value={popupText}
                onChange={handleInputChange(setPopupText)}
                placeholder="Texto de exibição"
              /> */}
              <LimitedTextarea
                value={popupText}
                onChange={setPopupText}
                placeholder="Texto de exibição"
              />
            </FormGroup>
          )}
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="primary" onClick={handleSubmit} disabled={loading} style={{ minWidth: 120 }}>
          {loading ? <Spinner size="sm" /> : 'Salvar'}
        </Button>
        <Button color="secondary" onClick={handleClose}>
          Cancelar
        </Button>
      </ModalFooter>
    </Modal>
  );
}
